import { useState, useEffect } from 'react';

interface Permission {
    id: string;
    type: 'READ' | 'WRITE' | 'UPDATE' | 'DELETE';
    name: string;
    createdAt: string;
    updatedAt: string;
    deleted: boolean;
}

interface StoredPermission {
    name: string;
    permissions: Permission[];
}

interface PermissionResult {
    isReadable: boolean;
    isDeletable: boolean;
    isWritable: boolean;
    isUpdatable: boolean;
}

const getStoredPermissions = (): StoredPermission[] => {
    try {
        const storedPermissions = localStorage.getItem('permissions');
        return storedPermissions ? JSON.parse(storedPermissions) : [];
    } catch (error) {
        console.error('Error parsing permissions from localStorage', error);
        return [];
    }
};

const useIsPermission = (permissionName: string): PermissionResult => {
    const [permissionsData, setPermissionsData] =
        useState<StoredPermission[]>(getStoredPermissions);

    useEffect(() => {
        const fetchPermissions = () => {
            setPermissionsData(getStoredPermissions());
        };

        window.addEventListener('storage', fetchPermissions);
        return () => {
            window.removeEventListener('storage', fetchPermissions);
        };
    }, []);

    const resource = permissionsData.find(item => item.name === permissionName);

    const permissionTypes = resource?.permissions?.map(p => p.type) || [];

    return {
        isReadable: permissionTypes.includes('READ'),
        isDeletable: permissionTypes.includes('DELETE'),
        isWritable: permissionTypes.includes('WRITE'),
        isUpdatable: permissionTypes.includes('UPDATE'),
    };
};

export default useIsPermission;
